"use client";

import { useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { ShoppingBag, Check } from "lucide-react";
import Price from "@/components/ui/price";
import { cn } from "@/lib/cn";
import { formatMoney } from "@/lib/format";
import type { Product } from "@/lib/types";

type Props = {
  product: Product;
  className?: string;
  sizes?: string;
};

export default function ProductCard({ product, className, sizes }: Props) {
  const { handle, title, vendor, featuredImage, priceRange, compareAtPriceRange, tags } = product;
  const compareAt = compareAtPriceRange?.minVariantPrice ?? null;
  const isNew = tags.includes("new");
  const isSale =
    compareAt != null &&
    Number(compareAt.amount) > Number(priceRange.minVariantPrice.amount);
  const savings =
    isSale && compareAt
      ? {
          amount: (
            Number(compareAt.amount) - Number(priceRange.minVariantPrice.amount)
          ).toFixed(2),
          currencyCode: compareAt.currencyCode,
        }
      : null;

  const [added, setAdded] = useState(false);
  const handleAdd = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setAdded(true);
    setTimeout(() => setAdded(false), 1500);
  };

  return (
    <Link
      href={`/products/${handle}`}
      className={cn(
        "group flex flex-col overflow-hidden rounded-none bg-[color:var(--color-surface)] shadow-soft transition-all hover:shadow-lift",
        className,
      )}
    >
      <div className="relative aspect-square overflow-hidden bg-[color:var(--color-surface-alt)]">
        <Image
          src={featuredImage.url}
          alt={featuredImage.altText ?? title}
          fill
          sizes={sizes ?? "(min-width: 1024px) 25vw, (min-width: 640px) 33vw, 50vw"}
          className="object-cover transition-transform duration-500 group-hover:scale-[1.04]"
        />
        {isSale && savings && (
          <span className="absolute left-2.5 top-2.5 inline-flex items-center rounded-[var(--radius-xs)] bg-[color:var(--color-sale)] px-2 py-1 text-[11px] font-bold uppercase tracking-wide text-white shadow-soft">
            Save {formatMoney(savings)}
          </span>
        )}
        {isNew && !isSale && (
          <span className="absolute left-2.5 top-2.5 inline-flex items-center rounded-[var(--radius-xs)] bg-[color:var(--color-secondary)] px-2 py-1 text-[11px] font-bold uppercase tracking-wide text-white shadow-soft">
            New
          </span>
        )}
      </div>
      <div className="flex flex-col gap-1.5 p-3">
        <div className="text-[10px] uppercase tracking-[0.14em] text-[color:var(--color-muted)]">
          {vendor}
        </div>
        <h3 className="text-sm font-semibold leading-snug line-clamp-2 min-h-[2.5rem]">
          {title}
        </h3>
        <div className="flex items-baseline gap-1.5">
          <Price amount={priceRange.minVariantPrice} compareAt={compareAt} size="sm" />
          <span className="text-[11px] text-[color:var(--color-muted)]">/ m²</span>
        </div>
        <button
          type="button"
          onClick={handleAdd}
          aria-live="polite"
          className={cn(
            "mt-2 inline-flex h-9 items-center justify-center gap-1.5 rounded-[var(--radius-sm)] text-xs font-semibold uppercase tracking-wide transition-colors",
            added
              ? "bg-[color:var(--color-success)] text-white"
              : "bg-[color:var(--color-sale)] text-white hover:bg-[color:var(--color-sale-dark)]",
          )}
        >
          {added ? (
            <>
              <Check size={14} /> Added
            </>
          ) : (
            <>
              <ShoppingBag size={14} /> Add to basket
            </>
          )}
        </button>
      </div>
    </Link>
  );
}
